import { useEffect, useState } from "react";

type Params = {
  articleId: string;
  isStatic: boolean;
  isActive: boolean;
  onVisibilityChange?: (visible: boolean) => void;
  onShow?: () => void;
  onHide?: () => void;
};

export function useStickyBarVisibility({
  articleId,
  isStatic,
  isActive,
  onVisibilityChange,
  onShow,
  onHide,
}: Params) {
  const [isVisible, setIsVisible] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (isStatic) return;

    const handleScroll = () => {
      const articleEl = document.getElementById(articleId);
      if (!articleEl) return;

      const winTop = window.scrollY;
      const headerHeight =
        parseInt(
          getComputedStyle(document.documentElement).getPropertyValue(
            "--headerHeight",
          ),
        ) || 64;

      // Find threshold: prioritize inline action bar, then featured image, then top of article
      const inlineActionBar = articleEl.querySelector(".v_articleActionsBar");

      const featuredImage = articleEl.querySelector(
        'div[class*="ArticleCarousel"], div[class*="ArticleImage"]',
      );

      let threshold: number;

      if (inlineActionBar) {
        const rect = inlineActionBar.getBoundingClientRect();
        threshold = rect.bottom + winTop - headerHeight;
      } else if (featuredImage) {
        const rect = featuredImage.getBoundingClientRect();
        threshold = rect.top + winTop - headerHeight;
      } else {
        const rect = articleEl.getBoundingClientRect();
        threshold = rect.top + winTop - headerHeight;
      }

      // Visibility logic: only show if scrolled past threshold AND this article is active
      const shouldBeVisible = winTop > threshold && isActive;

      if (shouldBeVisible !== isVisible) {
        setIsVisible(shouldBeVisible);
        onVisibilityChange?.(shouldBeVisible);

        if (shouldBeVisible) {
          document.documentElement.classList.add("v_showingStickySummary");
          onShow?.();
        } else {
          const activeBars = document.querySelectorAll(".v_showStickyBar");
          if (activeBars.length <= 1) {
            document.documentElement.classList.remove("v_showingStickySummary");
          }
          onHide?.();
        }
      }

      // Progress logic
      // Find end point: last tag
      const tags = articleEl.querySelectorAll('[data-testid="tag-wrapper"]');
      const lastTag =
        tags.length > 0
          ? tags[tags.length - 1].querySelector("span:last-of-type")
          : null;

      const start = threshold;
      const current = winTop;

      if (lastTag) {
        const end = lastTag.getBoundingClientRect().top + winTop - headerHeight;

        if (current < start) {
          setProgress(0);
        } else if (current > end) {
          setProgress(100);
        } else {
          const p = ((current - start) / (end - start)) * 100;
          setProgress(Math.max(0, Math.min(100, p)));
        }
      } else {
        // Fallback progress based on article height if no tags found
        const end =
          articleEl.getBoundingClientRect().bottom + winTop - headerHeight;

        const p = ((current - start) / (end - start)) * 100;
        setProgress(Math.max(0, Math.min(100, p)));
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [
    articleId,
    isStatic,
    isActive,
    isVisible,
    onVisibilityChange,
    onShow,
    onHide,
  ]);

  return { isVisible, progress };
}
